import { useState, useCallback, type KeyboardEvent } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'

interface TagInputProps {
  value: string[]
  onChange: (values: string[]) => void
  placeholder?: string
}

export function TagInput({
  value,
  onChange,
  placeholder = 'Type and press Enter...',
}: TagInputProps) {
  const [inputValue, setInputValue] = useState('')

  const addTag = useCallback(
    (tag: string) => {
      const trimmed = tag.trim()
      if (!trimmed) return
      if (value.includes(trimmed)) return
      onChange([...value, trimmed])
    },
    [value, onChange]
  )

  const removeTag = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index))
    },
    [value, onChange]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault()
        addTag(inputValue)
        setInputValue('')
      } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
        removeTag(value.length - 1)
      }
    },
    [inputValue, value, addTag, removeTag]
  )

  return (
    <div className="flex flex-col gap-2 rounded-md border border-input p-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag, index) => (
            <Badge
              key={tag}
              variant="secondary"
              className="gap-1 pr-1 text-sm"
            >
              {tag}
              <button
                type="button"
                className="ml-0.5 rounded-full p-0.5 cursor-pointer hover:bg-muted-foreground/20"
                onClick={() => removeTag(index)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Input
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="border-0 p-0 shadow-none focus-visible:ring-0"
      />
    </div>
  )
}
